import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Send, Copy, CheckCircle2, XCircle, Clock, Ban, ArrowLeft } from "lucide-react";

import { useAuth } from "@/hooks/use-auth";
import { hasAnyAdmin } from "@/lib/auth-status.functions";
import {
  createAgreementInvite,
  listAgreementInvites,
  revokeAgreementInvite,
} from "@/lib/agreements.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/agreements")({
  component: AgreementsPage,
  head: () => ({ meta: [{ title: "agreements — what admin" }] }),
});

const DEFAULT_AGREEMENT = `what Finland Oy — Partnership Agreement

1. Parties
This agreement is between what Finland Oy ("What") and the business named in this invitation ("Partner").

2. Listing
What will list the Partner on the what platform in accordance with the editorial standards published at whatfinland.com.

3. Commission & Fees
Commission and fees are as separately agreed in writing.

4. Term
This agreement starts on the date of acceptance and continues until terminated by either party with 30 days' written notice.

5. Data
Partner data will be processed in accordance with the what privacy policy.

6. Governing Law
This agreement is governed by the laws of Finland.

By accepting below, the signatory confirms they are authorised to enter into this agreement on behalf of the Partner.`;

function AgreementsPage() {
  const { user } = useAuth();
  const createFn = useServerFn(createAgreementInvite);
  const listFn = useServerFn(listAgreementInvites);
  const revokeFn = useServerFn(revokeAgreementInvite);

  const [businessName, setBusinessName] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [version, setVersion] = useState("1.0");
  const [body, setBody] = useState(DEFAULT_AGREEMENT);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    link: string;
    emailSent: boolean;
    emailError: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const adminCheck = useQuery({
    queryKey: ["any-admin"],
    queryFn: () => hasAnyAdmin(),
  });

  const invites = useQuery({
    queryKey: ["agreement-invites"],
    queryFn: () => listFn(),
    enabled: !!user,
  });

  if (!user) {
    return <CenteredText>please sign in.</CenteredText>;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    setSubmitting(true);
    try {
      const res = await createFn({
        data: {
          businessName: businessName.trim(),
          businessEmail: businessEmail.trim(),
          agreementVersion: version.trim(),
          agreementBody: body.trim(),
        },
      });
      setResult({ link: res.link, emailSent: res.emailSent, emailError: res.emailError });
      setBusinessName("");
      setBusinessEmail("");
      invites.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed to send invite.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevoke(id: string) {
    if (!confirm("revoke this invitation? the link will stop working.")) return;
    try {
      await revokeFn({ data: { id } });
      invites.refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : "failed to revoke.");
    }
  }

  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> back to admin
        </Link>

        <h1 className="text-2xl sm:text-3xl font-semibold mb-1">partnership agreements</h1>
        <p className="text-muted-foreground mb-8">
          send agreement invitations to businesses. recipients click a unique link, review the
          terms, and accept. all consents are timestamped with ip + user-agent.
        </p>

        <section className="border rounded-xl p-6 mb-10">
          <h2 className="text-lg font-semibold mb-4">send new invitation</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">business name</label>
                <Input
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  required
                  maxLength={200}
                  placeholder="e.g. Café Saima"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">business email</label>
                <Input
                  type="email"
                  value={businessEmail}
                  onChange={(e) => setBusinessEmail(e.target.value)}
                  required
                  maxLength={255}
                  placeholder="owner@business.fi"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">agreement version</label>
              <Input
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                required
                maxLength={50}
                placeholder="1.0"
                className="max-w-[180px]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">agreement text</label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
                rows={14}
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                this exact text is snapshotted with the invite and shown to the recipient.
              </p>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              send invitation
            </Button>
          </form>

          {result && (
            <div className="mt-5 border rounded-lg p-4 bg-emerald-50 dark:bg-emerald-950/30">
              <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                invitation created
                {result.emailSent ? (
                  <span className="text-xs text-emerald-700 dark:text-emerald-400">
                    · email sent
                  </span>
                ) : (
                  <span className="text-xs text-amber-700 dark:text-amber-400">
                    · email failed ({result.emailError || "unknown"}) — share link manually
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2">
                <Input value={result.link} readOnly className="text-xs" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => copyLink(result.link)}
                >
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  {copied ? "copied" : "copy"}
                </Button>
              </div>
            </div>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-4">invitations</h2>
          {invites.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : invites.isError ? (
            <p className="text-sm text-destructive">
              {adminCheck.data ? "failed to load invites." : "admin access required."}
            </p>
          ) : (invites.data?.invites.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">no invitations sent yet.</p>
          ) : (
            <div className="border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">

                <thead className="bg-muted/50 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">business</th>
                    <th className="text-left px-4 py-3">version</th>
                    <th className="text-left px-4 py-3">status</th>
                    <th className="text-left px-4 py-3">created</th>
                    <th className="text-right px-4 py-3">actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.data!.invites.map((inv: any) => {
                    const now = Date.now();
                    const expired = new Date(inv.expires_at).getTime() < now;
                    const status = inv.revoked_at
                      ? { label: "revoked", icon: Ban, tone: "text-muted-foreground" }
                      : inv.accepted_at
                      ? { label: "accepted", icon: CheckCircle2, tone: "text-emerald-600" }
                      : expired
                      ? { label: "expired", icon: XCircle, tone: "text-amber-600" }
                      : { label: "pending", icon: Clock, tone: "text-blue-600" };
                    const Icon = status.icon;
                    return (
                      <tr key={inv.id} className="border-t">
                        <td className="px-4 py-3">
                          <div className="font-medium">{inv.business_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {inv.business_email}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {inv.agreement_version}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 ${status.tone}`}>
                            <Icon className="h-3.5 w-3.5" />
                            {status.label}
                          </span>
                          {inv.accepted_at && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              by {inv.accepted_signatory_name} ·{" "}
                              {new Date(inv.accepted_at).toLocaleString()}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {new Date(inv.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                copyLink(`${window.location.origin}/accept/${inv.token}`)
                              }
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            {!inv.accepted_at && !inv.revoked_at && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => handleRevoke(inv.id)}
                              >
                                revoke
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>

          )}
        </section>
      </div>
    </div>
  );
}

function CenteredText({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground">
      {children}
    </div>
  );
}
